import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Clock, MessageSquare, AlertCircle, Eye, EyeOff, Pencil, Trash2, Download, Monitor, X, UserCog, FileText, Copy } from 'lucide-react';
import RichTextEditor from '../components/RichTextEditor';
import DOMPurify from 'dompurify';

const statusLabel = {
  open: 'Offen',
  in_progress: 'In Bearbeitung',
  waiting: 'Wartend',
  followup: 'Wiedervorlage',
  closed: 'Geschlossen',
};

const statusColor = {
  open: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  waiting: 'bg-orange-100 text-orange-700',
  followup: 'bg-purple-100 text-purple-700',
  closed: 'bg-gray-100 text-gray-500',
};

const minsToHHMM = (mins) => {
  if (!mins || mins <= 0) return '00:00';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return String(h).padStart(2,'0') + ':' + String(m).padStart(2,'0');
};

const hhmmToMins = (hhmm) => {
  if (!hhmm) return 0;
  const [h, m] = hhmm.split(':').map(Number);
  return (h * 60) + m;
};

const calcDurationMins = (start, end) => {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  return (eh * 60 + em) - (sh * 60 + sm);
};

function ServiceberichtBlock({ ticketId }) {
  const [report, setReport] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [form, setForm] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  const SERVICE_TYPES = ['Vor Ort Service','Remote Service','Telefon Service','Fernwartung','Office'];

  function calcDuration(start, end) {
    if (!start || !end) return '';
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const mins = (eh * 60 + em) - (sh * 60 + sm);
    if (mins <= 0) return '';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h === 0) return m + ' Min';
    if (m === 0) return h + ' Std';
    return h + ' Std ' + m + ' Min';
  }

  function today() { return new Date().toISOString().split('T')[0]; }
  function formatDateDE(d) {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  async function loadReport() {
    const api = (await import('../utils/api')).default;
    try {
      const res = await api.get('/service-reports/by-ticket/' + ticketId);
      setReport(res.data);
    } catch (_) {
      setReport(null);
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => { loadReport(); }, [ticketId]);

  async function openCreate() {
    setError(''); setSuccess('');
    const api = (await import('../utils/api')).default;
    try {
      const res = await api.get('/tickets/' + ticketId);
      const td = res.data;
      setForm({ ticket_id: ticketId, service_date: today(), service_type: 'Vor Ort Service', time_start: '', time_end: '', duration: '', work_description: '', salutation: td.contact_salutation ? (td.contact_salutation === 'Frau' ? 'Sehr geehrte Frau ' + (td.contact_name || '').split(' ').pop() + ',' : 'Sehr geehrter Herr ' + (td.contact_name || '').split(' ').pop() + ',') : '', contact_name: td.contact_name || '', recipient_email: td.contact_email || td.customer_email || '' });
    } catch (_) {
      setForm({ ticket_id: ticketId, service_date: today(), service_type: 'Vor Ort Service', time_start: '', time_end: '', duration: '', work_description: '', salutation: '', contact_name: '', recipient_email: '' });
    }
    setShowForm(true);
  }

  function openEdit() {
    setError(''); setSuccess('');
    setForm({ ticket_id: ticketId, service_date: report.service_date ? report.service_date.split('T')[0] : today(), service_type: report.service_type, time_start: report.time_start || '', time_end: report.time_end || '', duration: report.duration || '', work_description: report.work_description, salutation: report.salutation || '', contact_name: report.contact_name || '', recipient_email: report.recipient_email || '' });
    setShowForm(true);
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'time_start' || name === 'time_end') {
        updated.duration = calcDuration(name === 'time_start' ? value : prev.time_start, name === 'time_end' ? value : prev.time_end);
      }
      return updated;
    });
  }

  async function handleSave() {
    setSaving(true); setError('');
    const api = (await import('../utils/api')).default;
    try {
      if (report) { await api.put('/service-reports/' + report.id, form); }
      else { await api.post('/service-reports', form); }
      setShowForm(false); setSuccess('Bericht gespeichert.');
      loadReport();
    } catch (e) { setError(e.response?.data?.error || 'Fehler beim Speichern.'); }
    finally { setSaving(false); }
  }

  async function handleSend() {
    if (!window.confirm('Bericht jetzt per E-Mail versenden?')) return;
    setSending(true); setError('');
    const api = (await import('../utils/api')).default;
    try {
      await api.post('/service-reports/' + report.id + '/send');
      setSuccess('Bericht erfolgreich versendet.'); loadReport();
    } catch (e) { setError(e.response?.data?.error || 'Fehler beim Versenden.'); }
    finally { setSending(false); }
  }

  const apiBase = 'https://portal.nxc-emge.de';

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm mt-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900 text-sm">Servicebericht</h3>
        {!loading && !showForm && (
          <div className="flex gap-2">
            {report ? (
              <>
                <button onClick={async () => {
                  const api = (await import('../utils/api')).default;
                  const res = await api.get('/service-reports/' + report.id + '/preview');
                  const blob = new Blob([res.data], { type: 'text/html' });
                  const url = URL.createObjectURL(blob);
                  window.open(url, '_blank');
                }} className="text-xs px-2 py-1 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600">Vorschau</button>
                <button onClick={openEdit} className="text-xs px-2 py-1 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600">Bearbeiten</button>
                <button onClick={handleSend} disabled={sending} className="text-xs px-2 py-1 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap">{sending ? '...' : (report.sent ? 'Erneut senden' : 'Senden')}</button>
              </>
            ) : (
              <button onClick={openCreate} className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700">+ Servicebericht erstellen</button>
            )}
          </div>
        )}
      </div>
      {error && <div className="mb-3 p-2 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs">{error}</div>}
      {success && <div className="mb-3 p-2 bg-green-50 border border-green-200 text-green-700 rounded-lg text-xs">{success}</div>}
      {loading && <div className="text-xs text-gray-400">Lade...</div>}
      {!loading && !showForm && report && (
        <div className="text-xs text-gray-600 space-y-1">
          <div className="flex gap-4">
            <span><span className="text-gray-400">Datum:</span> {formatDateDE(report.service_date)}</span>
            <span><span className="text-gray-400">Typ:</span> {report.service_type}</span>
            <span><span className="text-gray-400">Zeit:</span> {report.time_start} bis {report.time_end} ({report.duration})</span>
          </div>
          <div className="flex gap-2 items-center">
            <span className="text-gray-400">Status:</span>
            {report.sent
              ? <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Gesendet {formatDateDE(report.sent_at)}</span>
              : <span className="px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium">Entwurf</span>}
          </div>
        </div>
      )}
      {!loading && !showForm && !report && <div className="text-xs text-gray-400">Noch kein Servicebericht vorhanden.</div>}
      {showForm && (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Datum *</label>
              <input type="date" name="service_date" value={form.service_date} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Service-Typ *</label>
              <select name="service_type" value={form.service_type} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                {SERVICE_TYPES.map(st => <option key={st}>{st}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Beginn</label>
              <input type="time" name="time_start" value={form.time_start} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Ende</label>
              <input type="time" name="time_end" value={form.time_end} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Gesamtzeit <span className="text-gray-400 font-normal">(auto, editierbar)</span></label>
              <input type="text" name="duration" value={form.duration} onChange={handleChange} placeholder="z.B. 1 Std 30 Min" className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Durchgefuehrte Arbeiten *</label>
              <textarea name="work_description" value={form.work_description} onChange={handleChange} rows={4} className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="col-span-2 border-t border-gray-100 pt-2">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">E-Mail</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Anrede</label>
              <input type="text" name="salutation" value={form.salutation} onChange={handleChange} placeholder="Sehr geehrte Frau Mustermann," className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Ansprechpartner</label>
              <input type="text" name="contact_name" value={form.contact_name} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Empfaenger E-Mail</label>
              <input type="email" name="recipient_email" value={form.recipient_email} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
          <div className="flex gap-2 pt-2 border-t border-gray-100">
            <button onClick={handleSave} disabled={saving} className="bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700 text-xs font-medium disabled:opacity-50">{saving ? 'Speichern...' : 'Speichern'}</button>
            <button onClick={() => setShowForm(false)} className="px-4 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-xs text-gray-600">Abbrechen</button>
          </div>
        </div>
      )}
    </div>
  );
}


export default function TicketDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [editOwner, setEditOwner] = useState(false);
  const [editTitle, setEditTitle] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [ownerCustomers, setOwnerCustomers] = useState([]);
  const [ownerContacts, setOwnerContacts] = useState([]);
  const [newCustomerId, setNewCustomerId] = useState('');
  const [newContactId, setNewContactId] = useState('');
  const [ownerType, setOwnerType] = useState('customer');
  const [suppliers, setSuppliers] = useState([]);
  const [newSupplierId, setNewSupplierId] = useState('');
  const [newSupplierContactId, setNewSupplierContactId] = useState('');
  const [supplierContacts, setSupplierContacts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [totalMinutes, setTotalMinutes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [commentBody, setCommentBody] = useState('');
  const [commentType, setCommentType] = useState('comment');
  const [replyTemplates, setReplyTemplates] = useState([]);
  useEffect(() => {
    api.get('/reply-templates').then(res => setReplyTemplates(res.data)).catch(() => {});
  }, []);
  const applyTemplate = (tplId) => {
    if (!tplId) return;
    const tpl = replyTemplates.find(t => String(t.id) === String(tplId));
    if (tpl) setCommentBody(tpl.body);
  };
  const [newStatus, setNewStatus] = useState('');
  const [statusComment, setStatusComment] = useState('');
  const [followupAt, setFollowupAt] = useState('');
  const [useCommentTime, setUseCommentTime] = useState(false);
  const [commentTimeStart, setCommentTimeStart] = useState('');
  const [commentTimeEnd, setCommentTimeEnd] = useState('');
  const [commentTimeDuration, setCommentTimeDuration] = useState('');
  const [showInternal, setShowInternal] = useState(false);
  const [devices, setDevices] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteName, setDeleteName] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [editingTime, setEditingTime] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');
  const [editDuration, setEditDuration] = useState('');

  const load = async () => {
    try {
      const [ticketRes, commentsRes, totalRes, attachmentsRes] = await Promise.all([
        api.get('/tickets/' + id),
        api.get('/tickets/' + id + '/comments'),
        api.get('/time/ticket/' + id + '/total'),
        api.get('/attachments/ticket/' + id),
      ]);
      setTicket(ticketRes.data);
      setComments(commentsRes.data);
      setTotalMinutes(totalRes.data.total_minutes);
      setAttachments(attachmentsRes.data);
      setNewStatus(ticketRes.data.status);
      // Geraete laden
      try {
        const devicesRes = await api.get('/devices?customer_id=' + ticketRes.data.customer_id);
        setDevices(devicesRes.data);
      } catch (e) {}
    } catch (err) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  // Automatisch Dauer berechnen wenn Von/Bis geaendert
  useEffect(() => {
    const mins = calcDurationMins(commentTimeStart, commentTimeEnd);
    if (mins > 0) setCommentTimeDuration(minsToHHMM(mins));
  }, [commentTimeStart, commentTimeEnd]);

  // Automatisch Dauer berechnen beim Bearbeiten
  useEffect(() => {
    const mins = calcDurationMins(editStart, editEnd);
    if (mins > 0) setEditDuration(minsToHHMM(mins));
  }, [editStart, editEnd]);

  const handleComment = async () => {
    if (!commentBody.trim()) return;
    let timePayload = {};
    if (useCommentTime) {
      if (!commentTimeStart || !commentTimeEnd) {
        toast.error('Von und Bis sind erforderlich');
        return;
      }
      const duration_minutes = hhmmToMins(commentTimeDuration);
      if (duration_minutes <= 0) {
        toast.error('Dauer muss groesser als 0 sein');
        return;
      }
      timePayload = { time_start: commentTimeStart, time_end: commentTimeEnd, duration_minutes };
    }
    try {
      await api.post('/tickets/' + id + '/comments', { type: commentType, body: commentBody, ...timePayload });
      setCommentBody('');
      setUseCommentTime(false);
      setCommentTimeStart('');
      setCommentTimeEnd('');
      setCommentTimeDuration('');
      toast.success('Kommentar gespeichert');
      load();
    } catch (err) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDeviceChange = async (deviceId) => {
    try {
      await api.patch('/tickets/' + id + '/device', {
        device_id: deviceId ? parseInt(deviceId) : null,
      });
      toast.success('Geraet gespeichert');
      load();
    } catch (err) {
      console.error(err);
      toast.error('Fehler beim Speichern');
    }
  };

  const handleStatus = async () => {
    if (newStatus === ticket.status) return;
    if (newStatus === 'followup' && !followupAt) {
      toast.error('Bitte Wiedervorlagedatum angeben');
      return;
    }
    try {
      await api.patch('/tickets/' + id + '/status', { status: newStatus, comment: statusComment, followup_at: followupAt || null });
      setStatusComment('');
      setFollowupAt('');
      toast.success('Status geaendert');
      load();
    } catch (err) {
      toast.error('Fehler beim Aendern');
    }
  };

  const handleEditCommentTime = (c) => {
    setEditingCommentId(c.id);
    setEditingTime(c.time_id);
    setEditStart(c.start_time ? c.start_time.slice(0,5) : '');
    setEditEnd(c.end_time ? c.end_time.slice(0,5) : '');
    setEditDuration(minsToHHMM(c.duration_minutes));
  };

  const handleSaveCommentTime = async () => {
    const duration_minutes = hhmmToMins(editDuration);
    if (duration_minutes <= 0) {
      toast.error('Dauer muss groesser als 0 sein');
      return;
    }
    try {
      await api.put('/tickets/' + id + '/comments/' + editingCommentId + '/time', {
        time_start: editStart,
        time_end: editEnd,
        duration_minutes,
      });
      setEditingTime(null);
      setEditingCommentId(null);
      toast.success('Zeiteintrag gespeichert');
      load();
    } catch (err) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleSaveTitle = async () => {
    if (!newTitle.trim()) { toast.error('Titel darf nicht leer sein'); return; }
    try {
      await api.patch('/tickets/' + id + '/title', { title: newTitle.trim() });
      toast.success('Titel gespeichert');
      setEditTitle(false);
      load();
    } catch (err) { toast.error('Fehler beim Speichern'); }
  };

  const openEditOwner = async () => {
    setEditOwner(true);
    const type = ticket.supplier_id ? 'supplier' : 'customer';
    setOwnerType(type);
    setNewCustomerId(ticket.customer_id || '');
    setNewContactId(ticket.contact_id || '');
    setNewSupplierId(ticket.supplier_id || '');
    setNewSupplierContactId(ticket.supplier_contact_id || '');
    try {
      const [custRes, supRes] = await Promise.all([
        api.get('/customers?limit=200'),
        api.get('/suppliers?limit=200')
      ]);
      setOwnerCustomers(custRes.data.customers || custRes.data || []);
      setSuppliers((supRes.data.suppliers || []).filter(s => s.is_active));
      if (ticket.customer_id) {
        const cRes = await api.get('/contacts/customer/' + ticket.customer_id);
        setOwnerContacts(cRes.data);
      }
      if (ticket.supplier_id) {
        const scRes = await api.get('/supplier-contacts/supplier/' + ticket.supplier_id);
        setSupplierContacts(scRes.data);
      }
    } catch (e) {}
  };

  const handleOwnerCustomerChange = async (custId) => {
    setNewCustomerId(custId);
    setNewContactId('');
    if (custId) {
      try {
        const res = await api.get('/contacts/customer/' + custId);
        setOwnerContacts(res.data);
      } catch (e) {}
    } else {
      setOwnerContacts([]);
    }
  };

  const handleSaveOwner = async () => {
    try {
      await api.patch('/tickets/' + id + '/owner', {
        customer_id: ownerType === 'customer' ? (newCustomerId || null) : null,
        contact_id: ownerType === 'customer' ? (newContactId || null) : null,
        supplier_id: ownerType === 'supplier' ? (newSupplierId || null) : null,
        supplier_contact_id: ownerType === 'supplier' ? (newSupplierContactId || null) : null,
      });
      toast.success('Besitzer geaendert');
      setEditOwner(false);
      load();
    } catch (err) { toast.error('Fehler beim Speichern'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/attachments/ticket/' + id, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAttachments(prev => [res.data, ...prev]);
      toast.success('Datei hochgeladen');
    } catch (err) {
      toast.error('Fehler beim Hochladen');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };
  const handleDeleteAttachment = async (attachId) => {
    if (!window.confirm('Anhang loeschen?')) return;
    try {
      await api.delete('/attachments/' + attachId);
      setAttachments(prev => prev.filter(a => a.id !== attachId));
      toast.success('Geloescht');
    } catch (err) { toast.error('Fehler'); }
  };
  const getAttachUrl = (id) => "/api/attachments/" + id + "/download?token=" + (localStorage.getItem("accessToken") || "");

  const formatBytes = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };
  const handleDelete = async () => {
    if (!deleteName || !deleteReason) return;
    setDeleting(true);
    try {
      await api.delete('/tickets/' + id);
      toast.success('Ticket geloescht');
      navigate('/tickets');
    } catch (err) {
      const msg = err.response?.data?.error || '';
      if (msg.includes('Servicebericht')) {
        toast.error('Ticket hat einen verknüpften Servicebericht und kann nicht gelöscht werden.');
      } else {
        toast.error('Fehler beim Löschen: ' + (msg || 'Unbekannter Fehler'));
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleDuplicate = async () => {
    try {
      const res = await api.post(`/tickets/${ticket.id}/duplicate`);
      toast.success('Ticket dupliziert: ' + res.data.ticket_number);
      navigate(`/tickets/${res.data.id}`);
    } catch (err) {
      toast.error('Fehler beim Duplizieren');
    }
  };
  const visibleComments = comments.filter(c => showInternal ? true : c.type !== 'internal_note');

  if (loading) return <div className="text-gray-400 text-sm">Laden...</div>;
  if (!ticket) return <div className="text-gray-400 text-sm">Ticket nicht gefunden</div>;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <button onClick={() => navigate('/tickets')}
          className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-600 transition-colors">
          <ArrowLeft size={16} /> Zurueck
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => navigate(`/tickets/${ticket.id}/summary-preview`)}
            className="flex items-center gap-2 text-xs text-teal-600 hover:text-teal-700 hover:bg-teal-50 px-3 py-1.5 rounded-lg transition-colors border border-teal-200">
            <FileText size={14} /> Zusammenfassung
          </button>
          <button onClick={handleDuplicate}
            className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 px-3 py-1.5 rounded-lg transition-colors border border-gray-200">
            <Copy size={14} /> Duplizieren
          </button>
          <button onClick={() => setShowDeleteModal(true)}
            className="flex items-center gap-2 text-xs text-red-400 hover:text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors">
            <Trash2 size={14} /> Ticket loeschen
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <span className="text-xl font-bold text-gray-700 font-mono">{ticket.ticket_number}</span>
            <span className={'text-xs font-medium px-2.5 py-1 rounded-full ' + (statusColor[ticket.status] || '')}>
              {statusLabel[ticket.status]}
            </span>
          </div>
          {editTitle ? (
            <div className="flex items-center gap-2 mt-1">
              <input value={newTitle} onChange={e => setNewTitle(e.target.value)}
                className="flex-1 text-xl font-bold border border-primary-300 rounded-xl px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
                onKeyDown={e => { if (e.key === 'Enter') handleSaveTitle(); if (e.key === 'Escape') setEditTitle(false); }}
                autoFocus />
              <button onClick={handleSaveTitle} className="bg-primary-600 text-white text-xs font-medium px-3 py-1.5 rounded-lg">Speichern</button>
              <button onClick={() => setEditTitle(false)} className="text-gray-400 text-xs px-2 py-1.5">Abbrechen</button>
            </div>
          ) : (
            <h2 className="text-2xl font-bold text-gray-900 cursor-pointer hover:text-primary-600 transition-colors group flex items-center gap-2"
              onClick={() => { setNewTitle(ticket.title); setEditTitle(true); }}>
              {ticket.title}
              <Pencil size={14} className="opacity-0 group-hover:opacity-40 transition-opacity" />
            </h2>
          )}
          {editOwner ? (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <select value={ownerType} onChange={e => setOwnerType(e.target.value)}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="customer">Kunde</option>
                <option value="supplier">Lieferant</option>
              </select>
              {ownerType === 'customer' ? (
                <>
                  <select value={newCustomerId} onChange={e => handleOwnerCustomerChange(e.target.value)}
                    className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                    <option value="">Kein Kunde</option>
                    {ownerCustomers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <select value={newContactId} onChange={e => setNewContactId(e.target.value)}
                    className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                    <option value="">Kein Ansprechpartner</option>
                    {ownerContacts.filter(c => c.is_active).map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
                  </select>
                </>
              ) : (
                <>
                  <select value={newSupplierId} onChange={async e => { setNewSupplierId(e.target.value); if (e.target.value) { const r = await api.get('/supplier-contacts/supplier/' + e.target.value); setSupplierContacts(r.data); } else setSupplierContacts([]); setNewSupplierContactId(''); }}
                    className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                    <option value="">Kein Lieferant</option>
                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  <select value={newSupplierContactId} onChange={e => setNewSupplierContactId(e.target.value)}
                    className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                    <option value="">Kein Ansprechpartner</option>
                    {supplierContacts.map(sc => <option key={sc.id} value={sc.id}>{sc.first_name} {sc.last_name}</option>)}
                  </select>
                </>
              )}
              <button onClick={handleSaveOwner} className="bg-primary-500 text-white text-xs font-medium px-3 py-1.5 rounded-lg">Speichern</button>
              <button onClick={() => setEditOwner(false)} className="text-gray-400 text-xs px-2 py-1.5">Abbrechen</button>
            </div>
          ) : (
            <p className="text-sm text-gray-500 mt-1 flex items-center gap-2">
              {ticket.supplier_id ? (ticket.supplier_name || 'Lieferant') + (ticket.supplier_contact_name ? ' · ' + ticket.supplier_contact_name : '') : (ticket.customer_name || '') + (ticket.contact_name ? ' · ' + ticket.contact_name : '')}
              <button onClick={openEditOwner} className="text-gray-300 hover:text-primary-500 transition-colors">
                <UserCog size={14} />
              </button>
            </p>
          )}
          {ticket.device_name && (
            <div className="flex items-center gap-1.5 mt-1">
              <Monitor size={13} className="text-gray-400" />
              <button
                onClick={() => navigate('/tickets?device_id=' + ticket.device_id + '&device_name=' + encodeURIComponent(ticket.device_name))}
                className="text-sm font-semibold text-gray-600 hover:text-primary-600 hover:underline transition-colors"
              >{ticket.device_name}</button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 bg-white border border-gray-100 rounded-xl p-3 shadow-sm self-start">
          <Clock size={16} className="text-teal-500" />
          <span className="text-sm font-semibold text-gray-700">{minsToHHMM(totalMinutes)}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare size={16} className="text-gray-400" />
                <h3 className="font-semibold text-gray-900 text-sm">Verlauf</h3>
              </div>
              <button
                onClick={() => setShowInternal(!showInternal)}
                className={'flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ' + (showInternal ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200')}
              >
                {showInternal ? <Eye size={13} /> : <EyeOff size={13} />}
                Interne Notizen
              </button>
            </div>
            <div className="p-4 border-t border-gray-100">
              <div className="flex flex-wrap gap-2 mb-3">
                <button onClick={() => setCommentType('comment')}
                  className={'text-xs px-3 py-1.5 rounded-full font-medium transition-colors ' + (commentType === 'comment' ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200')}
                >Kommentar</button>
                <button onClick={() => setCommentType('internal_note')}
                  className={'text-xs px-3 py-1.5 rounded-full font-medium transition-colors ' + (commentType === 'internal_note' ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200')}
                >Interne Notiz</button>
                {replyTemplates.length > 0 && (
                  <select
                    onChange={e => { applyTemplate(e.target.value); e.target.value = ''; }}
                    defaultValue=""
                    className="w-full sm:w-auto sm:ml-auto text-xs px-3 py-1.5 rounded-full font-medium bg-gray-100 text-gray-500 hover:bg-gray-200 border-0 focus:outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer"
                  >
                    <option value="" disabled>Vorlage einfügen...</option>
                    {replyTemplates.map(t => (
                      <option key={t.id} value={t.id}>{t.title}</option>
                    ))}
                  </select>
                )}
              </div>
              <textarea value={commentBody} onChange={e => setCommentBody(e.target.value)}
                placeholder="Kommentar eingeben..." rows={3}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none bg-gray-50" />
              <label className="flex items-center gap-2 mt-2 text-xs font-medium text-gray-500 cursor-pointer select-none">
                <input type="checkbox" checked={useCommentTime} onChange={e => setUseCommentTime(e.target.checked)}
                  className="rounded border-gray-300 text-primary-500 focus:ring-primary-500" />
                Zeit erfassen
              </label>
              {useCommentTime && (
                <div className="mt-2 flex flex-wrap gap-2 items-end">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Von</label>
                    <input type="time" value={commentTimeStart} onChange={e => setCommentTimeStart(e.target.value)}
                      className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-gray-50" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Bis</label>
                    <input type="time" value={commentTimeEnd} onChange={e => setCommentTimeEnd(e.target.value)}
                      className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-gray-50" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Dauer (HH:MM)</label>
                    <input type="text" value={commentTimeDuration} onChange={e => setCommentTimeDuration(e.target.value)}
                      placeholder="00:00"
                      className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-gray-50 font-mono w-24" />
                  </div>
                </div>
              )}
              <button onClick={handleComment}
                className="mt-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors shadow-sm">
                Speichern
              </button>
            </div>
            <div className="divide-y divide-gray-50">
              {visibleComments.map(c => (
                <div key={c.id} className={'p-4 ' + (c.type === 'internal_note' ? 'bg-amber-50/50' : '')}>
                  {c.type === 'status_change' && (
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <AlertCircle size={13} className="shrink-0" />
                      <span>{c.user_name} · {statusLabel[c.old_status]} &rarr; {statusLabel[c.new_status]}</span>
                      {c.body && <span className="text-gray-300">· {c.body}</span>}
                      <span className="ml-auto shrink-0">{new Date(c.created_at).toLocaleString('de-DE')}</span>
                    </div>
                  )}
                  {c.type === 'created' && (
                    <div className="text-xs text-gray-300">
                      Ticket erstellt von {c.user_name} · {new Date(c.created_at).toLocaleString('de-DE')}
                    </div>
                  )}
                  {c.type === 'time_entry' && (
                    <div className="flex items-center gap-2 text-xs text-teal-600">
                      <Clock size={13} className="shrink-0" />
                      <span>{c.user_name} · {c.body}</span>
                      <span className="ml-auto shrink-0 text-gray-300">{new Date(c.created_at).toLocaleString('de-DE')}</span>
                    </div>
                  )}
                  {(c.type === 'comment' || c.type === 'internal_note') && (
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-semibold text-gray-700">{c.user_name}</span>
                        <div className="flex items-center gap-2">
                          {c.type === 'internal_note' && (
                            <span className="text-xs bg-amber-100 text-amber-600 px-2 py-0.5 rounded-full">Interne Notiz</span>
                          )}
                          <span className="text-xs text-gray-300">{new Date(c.created_at).toLocaleString('de-DE')}</span>
                        </div>
                      </div>
                      <div className="text-sm text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(c.body || '') }} />
                      {c.time_id && (
                        editingTime === c.time_id ? (
                          <div className="mt-2 bg-gray-50 rounded-xl p-3 space-y-2">
                            <div className="flex gap-2">
                              <input type="time" value={editStart} onChange={e => setEditStart(e.target.value)}
                                className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500" />
                              <input type="time" value={editEnd} onChange={e => setEditEnd(e.target.value)}
                                className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500" />
                              <input type="text" value={editDuration} onChange={e => setEditDuration(e.target.value)}
                                placeholder="00:00"
                                className="w-20 border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono" />
                            </div>
                            <div className="flex gap-2">
                              <button onClick={handleSaveCommentTime}
                                className="flex-1 bg-primary-500 text-white text-xs font-medium py-1.5 rounded-lg">Speichern</button>
                              <button onClick={() => { setEditingTime(null); setEditingCommentId(null); }}
                                className="flex-1 bg-gray-200 text-gray-600 text-xs font-medium py-1.5 rounded-lg">Abbrechen</button>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-2 flex items-center gap-1.5 text-xs text-teal-600">
                            <Clock size={12} className="shrink-0" />
                            <span className="font-mono">{c.start_time.slice(0,5)} - {c.end_time.slice(0,5)} ({minsToHHMM(c.duration_minutes)})</span>
                            <button onClick={() => handleEditCommentTime(c)} className="text-gray-400 hover:text-primary-500 transition-colors">
                              <Pencil size={12} />
                            </button>
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
            <h3 className="font-semibold text-gray-900 text-sm mb-3">Status aendern</h3>
            <select value={newStatus} onChange={e => setNewStatus(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 mb-2 bg-gray-50">
              <option value="open">Offen</option>
              <option value="in_progress">In Bearbeitung</option>
              <option value="waiting">Wartend</option>
              <option value="followup">Wiedervorlage</option>
                          <option value="closed">Geschlossen</option>
            </select>
            {newStatus === 'followup' && (
              <div className="mb-2 mt-2">
                <label className="block text-xs text-gray-400 mb-1">Wiedervorlage am *</label>
                <input type="datetime-local" value={followupAt} onChange={e => setFollowupAt(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-gray-50" />
              </div>
            )}
            <input type="text" value={statusComment} onChange={e => setStatusComment(e.target.value)}
              placeholder="Kommentar (optional)"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 mb-2 bg-gray-50" />
            <button onClick={handleStatus}
              className="w-full bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors shadow-sm">
              Speichern
            </button>
          </div>

          {devices.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
              <h3 className="font-semibold text-gray-900 text-sm mb-3">Geraet</h3>
              <select
                value={ticket.device_id || ''}
                onChange={e => handleDeviceChange(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-gray-50"
              >
                <option value="">Kein Geraet</option>
                {devices.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.display_name || d.system_name || d.dns_name || 'Gerät ' + d.ninja_device_id} ({d.offline ? 'Offline' : 'Online'})
                  </option>
                ))}
              </select>
            </div>
          )}
          <ServiceberichtBlock ticketId={ticket?.id} />
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm mt-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900 text-sm">Anhaenge {attachments.length > 0 && <span className="text-xs text-gray-400">({attachments.length})</span>}</h3>
          <label className={"flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg cursor-pointer transition-colors " + (uploading ? "bg-gray-100 text-gray-400" : "bg-primary-50 text-primary-600 hover:bg-primary-100")}>
            {uploading ? "Laedt..." : "+ Datei"}
            <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} />
          </label>
        </div>
        {attachments.length === 0 && <p className="text-xs text-gray-400">Keine Anhaenge</p>}
        <div className="space-y-2">
          {attachments.map(a => (
            <div key={a.id} className="bg-gray-50 rounded-xl overflow-hidden">
              {/\.(png|jpg|jpeg|gif|webp)$/i.test(a.original_name) && (
                <a href={getAttachUrl(a.id)} target="_blank" rel="noreferrer">
                  <img src={getAttachUrl(a.id)} alt={a.original_name}
                    className="w-full max-h-48 object-contain bg-white cursor-pointer hover:opacity-90 transition-opacity" />
                </a>
              )}
              {/\.(pdf)$/i.test(a.original_name) && (
                <iframe src={getAttachUrl(a.id)} title={a.original_name}
                  className="w-full h-48 border-0" />
              )}
              <div className="flex items-center justify-between px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-gray-700 truncate">{a.original_name}</p>
                  <p className="text-xs text-gray-400">{formatBytes(a.size)}</p>
                </div>
                <div className="flex items-center gap-2 ml-2 shrink-0">
                  <a href={getAttachUrl(a.id)} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-primary-500 transition-colors">
                    <Download size={13} />
                  </a>
                  <button onClick={() => handleDeleteAttachment(a.id)} className="text-gray-400 hover:text-red-500 transition-colors">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-900">Ticket loeschen</h3>
              <button onClick={() => { setShowDeleteModal(false); setDeleteName(''); setDeleteReason(''); }}
                className="text-gray-400 hover:text-gray-600">
                <X size={18} />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Diese Aktion kann nicht rueckgaengig gemacht werden. Alle Kommentare und Zeiteintraege werden ebenfalls geloescht.
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Ihr Name zur Bestaetigung *
                </label>
                <input type="text" value={deleteName}
                  onChange={e => setDeleteName(e.target.value)}
                  placeholder={user?.first_name + ' ' + user?.last_name}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Grund der Loeschung *
                </label>
                <textarea value={deleteReason}
                  onChange={e => setDeleteReason(e.target.value)}
                  placeholder="Warum wird dieses Ticket geloescht?"
                  rows={3}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none" />
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <button
                onClick={handleDelete}
                disabled={!deleteName.trim() || !deleteReason.trim() || deleting ||
                  deleteName.trim().toLowerCase() !== (user?.first_name + ' ' + user?.last_name).toLowerCase()}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold py-2.5 rounded-xl transition-colors disabled:opacity-40"
              >
                {deleting ? 'Wird geloescht...' : 'Endgueltig loeschen'}
              </button>
              <button onClick={() => { setShowDeleteModal(false); setDeleteName(''); setDeleteReason(''); }}
                className="flex-1 border border-gray-200 text-gray-600 text-sm font-medium py-2.5 rounded-xl hover:bg-gray-50 transition-colors">
                Abbrechen
              </button>
            </div>
            {deleteName.trim() && deleteName.trim().toLowerCase() !== (user?.first_name + ' ' + user?.last_name).toLowerCase() && (
              <p className="text-xs text-red-400 mt-2 text-center">Name stimmt nicht ueberein</p>
            )}
          </div>
        </div>
      )}

      {ticket.description && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-4 shadow-sm">
          <div className="text-sm text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(ticket.description || '') }} />
        </div>
      )}
    </div>
  );
}
